import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  SafeAreaView,
  ActivityIndicator,
  RefreshControl,
  Alert,
  Platform,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../screens/ThemeContext';
import { LinearGradient } from 'expo-linear-gradient';
import { useAuth } from '../private/AuthContext';
import { collection, query, where, orderBy, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';
import AdminLayout from '../admin/web-ui/AdminLayout';

const NotificationTypes = {
  NEW_DOCUMENT: {
    icon: 'file-document',
    gradient: ['#4C6FFF', '#6B8CFF'],
  },
  SYSTEM_UPDATE: {
    icon: 'cog',
    gradient: ['#FF4C4C', '#FF6B6B'],
  },
  PAYMENT: {
    icon: 'cash',
    gradient: ['#4CAF50', '#6BBF6E'],
  },
  COMMENT: {
    icon: 'comment',
    gradient: ['#FF9800', '#FFA726'],
  },
};

export default function NotificationsScreen({ navigation }) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchNotifications = async () => {
    try {
      if (!user) return;

      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const notificationsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      setNotifications(notificationsData);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      Alert.alert('Erreur', 'Impossible de charger les notifications');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [user]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchNotifications();
  };

  const markAsRead = async (notificationId) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true,
      });
      
      setNotifications(prevNotifications =>
        prevNotifications.map(notification =>
          notification.id === notificationId
            ? { ...notification, read: true }
            : notification
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    Alert.alert(
      'Confirmation',
      'Voulez-vous supprimer cette notification ?',
      [
        {
          text: 'Annuler',
          style: 'cancel',
        },
        {
          text: 'Supprimer',
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteDoc(doc(db, 'notifications', notificationId));
              setNotifications(prevNotifications =>
                prevNotifications.filter(notification => notification.id !== notificationId)
              );
            } catch (error) {
              console.error('Error deleting notification:', error);
              Alert.alert('Erreur', 'Impossible de supprimer la notification');
            }
          },
        },
      ]
    );
  };

  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(notif => !notif.read);
      
      await Promise.all(
        unreadNotifications.map(notification =>
          updateDoc(doc(db, 'notifications', notification.id), {
            read: true,
          })
        )
      );

      setNotifications(prevNotifications =>
        prevNotifications.map(notification => ({
          ...notification,
          read: true,
        }))
      );

      Alert.alert('Succès', 'Toutes les notifications ont été marquées comme lues');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      Alert.alert('Erreur', 'Impossible de marquer les notifications comme lues');
    }
  };

  const deleteAllNotifications = () => {
    Alert.alert(
      'Confirmation',
      'Voulez-vous supprimer toutes les notifications ?',
      [
        {
          text: 'Annuler',
          style: 'cancel',
        },
        {
          text: 'Supprimer',
          style: 'destructive',
          onPress: async () => {
            try {
              await Promise.all(
                notifications.map(notification =>
                  deleteDoc(doc(db, 'notifications', notification.id))
                )
              );
              setNotifications([]);
              Alert.alert('Succès', 'Toutes les notifications ont été supprimées');
            } catch (error) {
              console.error('Error deleting all notifications:', error);
              Alert.alert('Erreur', 'Impossible de supprimer les notifications');
            }
          },
        },
      ]
    );
  };

  const renderNotification = (notification) => {
    const notificationType = NotificationTypes[notification.type] || NotificationTypes.SYSTEM_UPDATE;
  
    const handleNotificationPress = () => {
      if (!notification.read) {
        markAsRead(notification.id);
      }
      
      // Vérifier et utiliser le bon nom d'écran pour la navigation
      if (notification.data?.navigateTo) {
        // Remplacer 'DocumentDetailsScreen' par 'DocumentDetailsScreenUser'
        const screenName = notification.data.navigateTo === 'DocumentDetailsScreen' 
          ? 'DocumentDetailsScreenUser' 
          : notification.data.navigateTo;
          
        navigation.navigate(screenName, notification.data.params);
      }
    };
  
    return (
      <TouchableOpacity
        key={notification.id}
        style={[
          styles.notificationCard,
          { 
            backgroundColor: notification.read ? theme.surface : theme.surface + '80',
            borderColor: theme.border
          }
        ]}
        onPress={handleNotificationPress}
      >
        <LinearGradient
          colors={notificationType.gradient}
          style={styles.iconContainer}
        >
          <MaterialCommunityIcons 
            name={notificationType.icon} 
            size={24} 
            color="#fff" 
          />
        </LinearGradient>
  
        <View style={styles.notificationContent}>
          <Text style={[styles.notificationTitle, { color: theme.text }]}>
            {notification.title}
          </Text>
          <Text style={[styles.notificationMessage, { color: theme.textSecondary }]}>
            {notification.message}
          </Text>
          <Text style={[styles.notificationTime, { color: theme.textSecondary }]}>
            {notification.createdAt?.toDate().toLocaleDateString('fr-FR', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Text>
        </View>
  
        <TouchableOpacity
          style={styles.deleteButton}
          onPress={() => deleteNotification(notification.id)}
        >
          <MaterialCommunityIcons name="delete" size={20} color={theme.danger} />
        </TouchableOpacity>
  
        {!notification.read && (
          <View style={[styles.unreadDot, { backgroundColor: theme.primary }]} />
        )}
      </TouchableOpacity>
    );
  };

  const renderEmptyState = () => (
    <View style={styles.emptyContainer}>
      <MaterialCommunityIcons 
        name="bell-off-outline" 
        size={64} 
        color={theme.textSecondary} 
      />
      <Text style={[styles.emptyText, { color: theme.textSecondary }]}>
        Aucune notification
      </Text>
    </View>
  );

  // Sur web + utilisateur admin : on enveloppe dans AdminLayout (sidebar dashboard).
  const isWeb = typeof window !== 'undefined' && !!window.document;
  const isAdminWeb = isWeb && user?.userType === 'admin';

  const unreadCount = notifications.filter((n) => !n.read).length;

  const actionsBar = notifications.length > 0 ? (
    <View style={styles.headerActions}>
      <TouchableOpacity
        style={[styles.headerButton, { backgroundColor: theme.primary + '15' }]}
        onPress={markAllAsRead}
      >
        <MaterialCommunityIcons name="check-all" size={22} color={theme.primary} />
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.headerButton, { backgroundColor: (theme.danger || '#ef4444') + '15' }]}
        onPress={deleteAllNotifications}
      >
        <MaterialCommunityIcons name="delete-sweep" size={22} color={theme.danger || '#ef4444'} />
      </TouchableOpacity>
    </View>
  ) : null;

  const body = loading ? (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size="large" color={theme.primary} />
    </View>
  ) : notifications.length > 0 ? (
    notifications.map((n) => renderNotification(n))
  ) : (
    renderEmptyState()
  );

  // === Rendu dashboard (web admin) ===
  if (isAdminWeb) {
    return (
      <AdminLayout
        title="Notifications"
        subtitle={unreadCount > 0 ? `${unreadCount} non lue${unreadCount > 1 ? 's' : ''}` : 'Toutes lues'}
      >
        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 12 }}>
          {actionsBar}
        </View>
        <View style={{ gap: 10 }}>
          {body}
        </View>
      </AdminLayout>
    );
  }

  // === Rendu d'origine (mobile / user) ===
  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: theme.background }]}>
      <View style={[styles.header, { borderBottomColor: theme.border }]}>
        <View style={styles.headerTop}>
          <View style={styles.headerContent}>
            <TouchableOpacity
              onPress={() => navigation.goBack()}
              style={[styles.backButton, { backgroundColor: theme.surface, borderColor: theme.border }]}
            >
              <MaterialCommunityIcons name="arrow-left" size={24} color={theme.primary} />
            </TouchableOpacity>
            <Text style={[styles.headerTitle, { color: theme.text }]}>Notifications</Text>
          </View>
          {actionsBar}
        </View>
      </View>

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.primary} />
        </View>
      ) : (
        <ScrollView
          style={styles.container}
          contentContainerStyle={styles.contentContainer}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              colors={[theme.primary]}
            />
          }
        >
          {notifications.length > 0
            ? notifications.map((notification) => renderNotification(notification))
            : renderEmptyState()}
        </ScrollView>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    padding: 8,
    marginRight: 16,
    borderRadius: 12,
  },
  header: {
    padding: 16,
    borderBottomWidth: 1,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  headerActions: {
    flexDirection: 'row',
    gap: 8,
  },
  headerButton: {
    padding: 8,
    borderRadius: 8,
  },
  notificationCard: {
    flexDirection: 'row',
    padding: 16,
    borderRadius: 16,
    marginBottom: 12,
    borderWidth: 1,
    position: 'relative',
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  notificationContent: {
    flex: 1,
    marginRight: 8,
  },
  notificationTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  notificationMessage: {
    fontSize: 14,
    marginBottom: 8,
    lineHeight: 20,
  },
  notificationTime: {
    fontSize: 12,
  },
  deleteButton: {
    padding: 8,
    alignSelf: 'center',
  },
  unreadDot: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 16,
    marginTop: 16,
  },
});